import React, { useCallback } from 'react'

import { HiCheck } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

export interface WizardStepperStep {
  id: string
  label: string
}

interface WizardStepperProps {
  steps: ReadonlyArray<WizardStepperStep>
  currentIndex: number
  ariaLabel: string
  /**
   * When provided, completed steps become clickable so users can jump back.
   * Future steps stay locked — forward navigation goes through Continue.
   */
  onStepSelect?: (index: number) => void
}

/**
 * Horizontal labeled stepper (numbered circle → check on completion, connector
 * lines), following the design language of the monitors wizard header.
 */
export const WizardStepper: React.FC<WizardStepperProps> = ({
  steps,
  currentIndex,
  ariaLabel,
  onStepSelect,
}) => (
  <ol aria-label={ariaLabel} className="flex min-w-0 items-center">
    {steps.map((step, index) => (
      <React.Fragment key={step.id}>
        {/* Connectors are their own presentation li (an ol only permits li
            children) so they stay equal-width flex-1 siblings of the
            shrink-0 steps — nesting one inside a step's li would subtract
            that step's label width from its connector. */}
        {index > 0 && (
          <li
            role="presentation"
            aria-hidden={true}
            className={twMerge(
              'mx-2.5 h-[1.5px] min-w-4 flex-1 rounded-full transition-colors duration-300',
              index <= currentIndex
                ? 'bg-accent-500 dark:bg-accent-500/70'
                : 'bg-primary-200 dark:bg-primary-800',
            )}
          />
        )}
        <li
          className="shrink-0"
          aria-current={index === currentIndex ? 'step' : undefined}
        >
          <WizardStepperItem
            step={step}
            index={index}
            currentIndex={currentIndex}
            onStepSelect={onStepSelect}
          />
        </li>
      </React.Fragment>
    ))}
  </ol>
)

WizardStepper.displayName = 'Wizard.Stepper'

interface WizardStepperItemProps {
  step: WizardStepperStep
  index: number
  currentIndex: number
  onStepSelect?: (index: number) => void
}

const WizardStepperItem: React.FC<WizardStepperItemProps> = ({
  step,
  index,
  currentIndex,
  onStepSelect,
}) => {
  const active = index === currentIndex
  const complete = index < currentIndex
  const clickable = onStepSelect !== undefined && complete
  const handleClick = useCallback(() => {
    onStepSelect?.(index)
  }, [index, onStepSelect])

  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={handleClick}
      className={twMerge(
        'group flex h-8 items-center gap-2 rounded-md px-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500',
        // Extend the hit area to 40px vertically (visible height is 32px);
        // horizontal stays put so adjacent steps' targets never overlap.
        'relative after:absolute after:-inset-y-1 after:inset-x-0 after:content-[""]',
        clickable
          ? 'cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-800/40'
          : 'cursor-default',
      )}
    >
      <span
        className={twMerge(
          'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px] text-[11px] font-bold tabular-nums',
          'transition-[border-color,background-color,color] duration-300',
          complete &&
            'border-accent-600 bg-accent-600 text-white dark:border-accent-500 dark:bg-accent-500',
          active &&
            'border-accent-600 bg-white text-accent-700 dark:border-accent-500 dark:bg-primary-900 dark:text-accent-300',
          !active &&
            !complete &&
            'border-primary-300 bg-transparent text-primary-400 dark:border-primary-700 dark:text-primary-500',
        )}
      >
        {complete ? <HiCheck className="h-3 w-3" strokeWidth={3} /> : index + 1}
      </span>
      <span
        className={twMerge(
          'whitespace-nowrap text-[12.5px] transition-colors duration-300',
          active && 'font-semibold text-primary-900 dark:text-primary-100',
          complete &&
            'font-medium text-primary-600 group-hover:text-primary-800 dark:text-primary-300 dark:group-hover:text-primary-100',
          !active &&
            !complete &&
            'font-medium text-primary-400 dark:text-primary-500',
        )}
      >
        {step.label}
      </span>
    </button>
  )
}
